import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Boxes, TrendingDown,
  Plus, Edit3, Clock, Trash2, Download, FileText, History
} from "lucide-react";
import api, { money, errMsg } from "../lib/api";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import PurchaseInModal from "../components/PurchaseInModal";
import PurchaseInvoices from "./PurchaseInvoices";
import AddProductModal from "../components/AddProductModal";
import EditProductModal from "../components/EditProductModal";
import StockLedgerPage from "./StockLedgerPage";
import DailyStockOut from "./DailyStockOut";
import StockHistory from "./StockHistory";
import CustomSelect from "../components/ui/CustomSelect";
import { todayIST } from "../lib/datetime";
import useAutoRefresh from "../lib/useAutoRefresh";

// Owner rule (20 Oct 2026): these four are the only product types.
const CATEGORIES = ["All", "Retail", "Technical", "Disposable", "Electrical"];
// Deep-linkable sections: the Manager Desk links straight to ?section=daily_out
const SECTIONS = ["inventory", "purchase_invoices", "daily_out", "ledger", "stock_history"];
  // Two units: pack unit for Qty (e.g. 100ml), bottle/piece unit for Stock (e.g. 2 bottles)
  const qtyUnitOf = (p) => (p.unit || "").toLowerCase();
// Owner rule (Oct 2026): one main stock per product. This is the same number
// the Stock Audit counts and the API returns as current_stock, so every screen
// agrees.
const mainStockOf = (p) => Number(p.store_qty) || 0;
// One status definition for the table, the column filter and the CSV export.
const statusOf = (p) => {
  const onHand = mainStockOf(p);
  const min = p.min_stock ?? 0;
  if (onHand === 0) return "Out of Stock";
  if (min > 0 && onHand <= min) return "Low Stock";
  return "Active";
};

export default function InventoryHub() {
  // Which section is on screen lives in the URL, not in component state, so
  // another window can deep-link to it (?section=daily_out). It is derived
  // rather than stored: a stored copy would drift from the URL the moment
  // someone hit Back, and the tab bar would light up the wrong tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedSection = searchParams.get("section") || "inventory";
  const activeSection = SECTIONS.includes(requestedSection) ? requestedSection : "inventory";
  const setActiveSection = (id) =>
    setSearchParams(id === "inventory" ? {} : { section: id }, { replace: true });
  const [products, setProducts] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filters & Search
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [statusFilter, setStatusFilter] = useState("all"); // all | low_stock | out_of_stock

  // Column-level filters
  const [colFilters, setColFilters] = useState({
    vendor: "",
    name: "",
    brand: "",
    category: "All",
    qty: "",
    stock: "All",
    unitp: "All",
    status: "All",
  });
  const setCol = (key, val) => setColFilters((prev) => ({ ...prev, [key]: val }));

  // Modals
  const [showPurchaseInModal, setShowPurchaseInModal] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);

  const handleDeleteProduct = async (skuId, name) => {
    if (!window.confirm(`Are you sure you want to delete product '${name}'? This action cannot be undone.`)) {
      return;
    }
    try {
      await api.delete(`/inventory/product/${skuId}`);
      toast.success(`Deleted product '${name}'`);
      fetchMasterData(true);
      fetchKpis();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  useEffect(() => {
    fetchMasterData();
    fetchKpis();
  }, [statusFilter]);

  // Auto-refresh inventory + KPIs: poll + refocus, no manual button anywhere.
  // The shared hook keeps the handler in a ref, so it always sees the current
  // statusFilter without tearing the timer down on every filter change, and it
  // coalesces the focus + visibilitychange pair that fire together on a tab
  // switch into a single reload instead of two.
  useAutoRefresh(() => {
    fetchMasterData(true);
    fetchKpis();
  }, 30000);

  const [displayLimit, setDisplayLimit] = useState(100);

  // `silent` background refresh: keep whatever is currently on screen — the
  // rows, the scroll position and the focused column-filter inputs — mounted
  // while the replacement arrives, and swap the data in only once it is here.
  // The 30s poll and the refocus pull both pass true so whoever is looking at
  // the page never sees the table collapse into a skeleton. Only the first
  // paint (loading starts true) and an explicit statusFilter change show it.
  const fetchMasterData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      let url = `/inventory/master?category=All`;
      if (statusFilter !== "all") url += `&status=${statusFilter}`;
      const res = await api.get(url);
      const prepared = (res.data || []).map((p) => {
        return {
          ...p,
          _v: (p.vendor_name || "").toLowerCase(),
          _n: (p.name || "").toLowerCase(),
          _b: (p.brand || "").toLowerCase(),
        };
      });
      setProducts(prepared);
    } catch (e) {
      console.error(e);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchKpis = async () => {
    try {
      const res = await api.get("/inventory/dashboard-kpis");
      setKpis(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const filteredProducts = useMemo(() => {
    const list = Array.isArray(products) ? products : [];
    const v = (colFilters.vendor || "").trim().toLowerCase();
    const n = (colFilters.name || "").trim().toLowerCase();
    const b = (colFilters.brand || "").trim().toLowerCase();
    const qtyF = (colFilters.qty || "").trim();
    const cat = colFilters.category;
    const st = colFilters.status;
    const stockF = colFilters.stock || "All";
    const priceF = colFilters.unitp || "All";

    return list.filter((p) => {
      if (!p) return false;
      const p_v = p._v || (p.vendor_name || "").toLowerCase();
      const p_n = p._n || (p.name || "").toLowerCase();
      const p_b = p._b || (p.brand || "").toLowerCase();

      if (v && !p_v.includes(v)) return false;
      if (n && !p_n.includes(n)) return false;
      if (b && !p_b.includes(b)) return false;
      if (cat !== "All" && p.category !== cat) return false;
      if (qtyF) {
        // Exact numeric match: the dropdown offers exact pack sizes, so "10"
        // must not also match 100/1000 (the old substring test did).
        if (Number(p.qty) !== Number(qtyF)) return false;
      }
      if (stockF !== "All") {
        // One main stock per product — same figure as the Stock cell.
        const onHand = mainStockOf(p);
        if (stockF === "Out (0)" && onHand !== 0) return false;
        if (stockF === "1 – 5" && (onHand <= 0 || onHand > 5)) return false;
        if (stockF === "6 – 20" && (onHand <= 5 || onHand > 20)) return false;
        if (stockF === "20+" && onHand <= 20) return false;
      }
      if (priceF !== "All") {
        const up = p.unit_price || 0;
        if (priceF === "0" && up !== 0) return false;
        if (priceF === "Under 500" && (up === 0 || up >= 500)) return false;
        if (priceF === "500 – 1k" && (up < 500 || up >= 1000)) return false;
        if (priceF === "1k – 2.5k" && (up < 1000 || up >= 2500)) return false;
        if (priceF === "2.5k+" && up < 2500) return false;
      }
      if (st !== "All" && statusOf(p) !== st) return false;
      return true;
    });
  }, [products, colFilters]);

  const dropdownOptions = useMemo(() => {
    const list = Array.isArray(products) ? products : [];
    const brands = Array.from(new Set(list.map(p => (p.brand || "").trim()).filter(Boolean))).sort();
    const qtys = Array.from(new Set(list.map(p => p.qty).filter(q => q !== undefined && q !== null && q !== 0))).sort((a, b) => a - b);
    return {
      brands: ["All", ...brands],
      // "All" first like brands: without it the dropdown shows the
      // placeholder once a size is picked and can never be cleared back.
      qtys: ["All", ...qtys.map(q => String(q))],
    };
  }, [products]);

  const filteredMetrics = useMemo(() => {
    let stockUnits = 0;
    let totalValuation = 0;

    for (const p of filteredProducts) {
      const onHand = Number(p.store_qty) || 0;
      const price = p.unit_price || 0;
      // Owner rule (Oct 2026): one main stock, valued at unit price.
      const valuation = onHand * price;

      stockUnits += onHand;
      totalValuation += valuation;
    }

    return {
      count: filteredProducts.length,
      stockUnits,
      totalValuation,
    };
  }, [filteredProducts]);

  const handleExportLiveInventory = () => {
    if (loading) {
      toast.info("Inventory is still loading. Try again in a moment.");
      return;
    }
    if (!filteredProducts || filteredProducts.length === 0) {
      toast.error("No inventory items match the current filters");
      return;
    }

    // Quote only when needed so Excel keeps numbers as numbers, and add a BOM
    // so names with curly apostrophes / accented characters open correctly.
    const csvCell = (value) => {
      const text = value == null ? "" : String(value);
      return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const headers = [
      "Vendor",
      "Product Name",
      "Qty",
      "Brand",
      "Type",
      "Stock",
      "Min Stock",
      "Reorder Level",
      "Cost Price (INR)",
      "Unit Price (INR)",
      "Total Valuation (INR)",
      "Status",
    ];
    const rows = filteredProducts.map((p) => {
      const onHand = mainStockOf(p);
      const cost = Number(p.unit_cost) || 0;
      const price = Number(p.unit_price) || 0;
      const valuation = onHand * price;
      return [
        p.vendor_name || "Unassigned",
        p.name || "",
        p.qty ? `${Number(p.qty)} ${qtyUnitOf(p)}` : "—",
        p.brand || "Generic",
        // Type is the product category (Retail / Technical / Disposable /
        // Electrical). `ledger` is only technical/retail and was mislabelling
        // every Disposable item in the old export.
        p.category || "Retail",
        onHand,
        p.min_stock ?? 0,
        p.reorder_level ?? 0,
        cost.toFixed(2),
        price.toFixed(2),
        valuation.toFixed(2),
        statusOf(p),
      ].map(csvCell).join(",");
    });

    const csvData = "\uFEFF" + [headers.map(csvCell).join(","), ...rows].join("\r\n");
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Geetanjali_Live_Inventory_${todayIST()}.csv`);
    document.body.appendChild(link);
    link.click();
    // Keep the anchor alive briefly: some browsers cancel a blob download if
    // the element is removed in the same tick as the synthetic click.
    setTimeout(() => {
      link.remove();
      URL.revokeObjectURL(url);
    }, 1000);
    toast.success(`Exported ${filteredProducts.length} live inventory items!`);
  };

  return (
    <div className="max-w-[96%] mx-auto p-4 sm:p-6 space-y-6 animate-in fade-in duration-300">
      {/* Enterprise Inventory Hub Navigation Bar */}
      <div className="lss-card p-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="lss-overline text-amber-800">Geetanjali Enterprise ERP</div>
            <h1 className="font-serif-lux text-3xl font-bold tracking-tight text-slate-950 mt-0.5">
              Inventory & Supply Chain Suite
            </h1>
          </div>
          {activeSection !== "stock_history" && (
          <div className="flex flex-wrap gap-2 shrink-0">
            <button
              type="button"
              onClick={handleExportLiveInventory}
              className="px-4 py-2.5 text-xs font-extrabold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-md flex items-center gap-1.5 transition-all"
            >
              <Download className="w-4 h-4" /> Export Live Inventory
            </button>
            <button
              onClick={() => setShowAddProductModal(true)}
              className="lss-btn-gold px-4 py-2.5 text-xs font-extrabold uppercase tracking-wider shadow-md flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" /> Add Item
            </button>
            <button
              onClick={() => setActiveSection("purchase_invoices")}
              className="lss-btn-outline px-4 py-2.5 text-xs font-extrabold uppercase tracking-wider shadow-xs flex items-center gap-1.5"
            >
              <FileText className="w-4 h-4" /> Purchase Invoices
            </button>
          </div>
          )}
        </div>

        {/* Section Navigation Tabs */}
        <div className="flex gap-2 overflow-x-auto pt-2 border-t border-slate-200">
          {[
            { id: "inventory", label: "Inventory Master", icon: Boxes },
            { id: "purchase_invoices", label: "Purchase Invoices", icon: FileText },
            { id: "daily_out", label: "Daily Stock Out", icon: TrendingDown },
            { id: "ledger", label: "Stock Movement Ledger", icon: Clock },
            { id: "stock_history", label: "Stock History", icon: History },
          ].map((sec) => {
            const Icon = sec.icon;
            const isActive = activeSection === sec.id;
            return (
              <button
                key={sec.id}
                onClick={() => {
                  setActiveSection(sec.id);
                }}
                className={`relative px-4 py-2.5 text-xs font-extrabold uppercase tracking-wider rounded-xl transition-all flex items-center gap-2 shrink-0 ${
                  isActive
                    ? "bg-slate-950 text-amber-300 shadow-md shadow-slate-950/20"
                    : "text-slate-700 hover:text-slate-950 hover:bg-slate-100"
                }`}
              >
                <Icon className={`w-4 h-4 ${isActive ? "text-amber-400" : "text-slate-500"}`} />
                <span>{sec.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* KPI Cards Dashboard Summary */}
      {/* KPI Cards Dashboard Summary */}
      {kpis && activeSection !== "stock_history" && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="lss-card p-4">
            <span className="lss-overline block text-slate-500">Total SKUs</span>
            <span className="text-2xl font-extrabold text-slate-950 mt-1 block tabular">{kpis.total_products}</span>
          </div>
          <div className="lss-card p-4">
            <span className="lss-overline block text-slate-500">Total Stock</span>
            <span className="text-2xl font-extrabold text-slate-950 mt-1 block tabular">{Number(kpis.total_stock || 0).toLocaleString()} <span className="text-xs font-bold text-slate-500">units</span></span>
          </div>
          <div className="lss-card p-4">
            <span className="lss-overline block text-slate-500">Inventory Valuation</span>
            <span className="text-2xl font-extrabold text-slate-950 mt-1 block tabular">{money(kpis.total_inventory_value)}</span>
          </div>
          <div className={`lss-card p-4 cursor-pointer transition-all ${statusFilter === "low_stock" ? "bg-amber-500 text-slate-950 border-amber-600 shadow-amber-200" : "bg-amber-50/80 border-amber-200 text-amber-950"}`} onClick={() => setStatusFilter(statusFilter === "low_stock" ? "all" : "low_stock")}>
            <span className="lss-overline block">Low Stock Alert</span>
            <span className="text-2xl font-extrabold mt-1 block tabular">{kpis.low_stock_count} items</span>
          </div>
          <div className={`lss-card p-4 cursor-pointer transition-all ${statusFilter === "out_of_stock" ? "bg-rose-600 text-white border-rose-700 shadow-rose-200" : "bg-rose-50/80 border-rose-200 text-rose-950"}`} onClick={() => setStatusFilter(statusFilter === "out_of_stock" ? "all" : "out_of_stock")}>
            <span className="lss-overline block">Out of Stock</span>
            <span className="text-2xl font-extrabold mt-1 block tabular">{kpis.out_of_stock_count} items</span>
          </div>
          <div className="lss-card p-4">
            <span className="lss-overline block text-slate-500">Today's Stock In</span>
            <span className="text-2xl font-extrabold text-emerald-800 mt-1 block tabular">+{kpis.today_stock_in}</span>
          </div>
        </div>
      )}

      {/* Main Section Content Render */}
      {activeSection === "inventory" && (
        <div className="space-y-6">
          {/* Master Inventory Data Table */}
          <div className="lss-card overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/70">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                Product Master Registry ({filteredProducts.length})
              </span>
              <span className="text-xs text-slate-500 font-semibold">Real-Time Inventory Status</span>
            </div>

            {loading ? (
              <div className="p-4 space-y-4 animate-pulse">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="flex gap-4">
                    <div className="h-10 bg-slate-200 rounded w-1/6"></div>
                    <div className="h-10 bg-slate-200 rounded w-1/3"></div>
                    <div className="h-10 bg-slate-200 rounded w-1/6"></div>
                    <div className="h-10 bg-slate-200 rounded w-1/6"></div>
                    <div className="h-10 bg-slate-200 rounded w-1/6"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="overflow-auto max-h-[65vh]">
                {/* The header pins inside this bounded viewport: only the column
                    labels (Vendor, Product, Qty…) stay put while rows scroll, and
                    the filter row scrolls away with them. */}
                <table className="lss-table lss-sticky-table">
                  <thead>
                    {/* Row 1 — Column Labels (pinned while scrolling) */}
                    <tr className="lss-sticky-head bg-slate-950 text-amber-300">
                      <th className="text-left py-2.5 px-3 text-[10px] font-extrabold uppercase tracking-wider whitespace-nowrap">Vendor</th>
                      <th className="text-left py-2.5 px-3 text-[10px] font-extrabold uppercase tracking-wider whitespace-nowrap">Product</th>
                      <th className="text-right py-2.5 px-3 text-[10px] font-extrabold uppercase tracking-wider whitespace-nowrap">Qty</th>
                      <th className="text-left py-2.5 px-3 text-[10px] font-extrabold uppercase tracking-wider whitespace-nowrap">Brand</th>
                      <th className="text-left py-2.5 px-3 text-[10px] font-extrabold uppercase tracking-wider whitespace-nowrap">Type</th>
                      <th className="text-right py-2.5 px-3 text-[10px] font-extrabold uppercase tracking-wider whitespace-nowrap">Stock</th>
                      <th className="text-right py-2.5 px-3 text-[10px] font-extrabold uppercase tracking-wider whitespace-nowrap">Min</th>
                      <th className="text-right py-2.5 px-3 text-[10px] font-extrabold uppercase tracking-wider whitespace-nowrap">Unit Price</th>
                      <th className="text-right py-2.5 px-3 text-[10px] font-extrabold uppercase tracking-wider whitespace-nowrap text-emerald-400">Valuation</th>
                      <th className="text-left py-2.5 px-3 text-[10px] font-extrabold uppercase tracking-wider whitespace-nowrap">Status</th>
                      <th className="text-center py-2.5 px-3 text-[10px] font-extrabold uppercase tracking-wider whitespace-nowrap">Actions</th>
                    </tr>
                    {/* Row 2 — Filter Inputs (scrolls away; labels stay) */}
                    <tr className="lss-no-sticky bg-slate-50 border-b border-slate-200">
                      <th className="px-3 py-2">
                        <input type="text" value={colFilters.vendor} onChange={(e) => setCol("vendor", e.target.value)} placeholder="Filter vendor…" className="w-full text-xs px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-800 font-normal focus:outline-none focus:ring-2 focus:ring-amber-400/30 focus:border-amber-400 placeholder-slate-400 transition" />
                      </th>
                      <th className="px-3 py-2">
                        <input type="text" value={colFilters.name} onChange={(e) => setCol("name", e.target.value)} placeholder="Filter product…" className="w-full text-xs px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-800 font-normal focus:outline-none focus:ring-2 focus:ring-amber-400/30 focus:border-amber-400 placeholder-slate-400 transition" />
                      </th>
                      <th className="px-3 py-2">
                        <CustomSelect
                          options={dropdownOptions.qtys}
                          value={colFilters.qty ? String(colFilters.qty) : "All"}
                          onChange={(val) => setCol("qty", val === "All" ? "" : val)}
                          placeholder="All"
                          searchable
                        />
                      </th>
                      <th className="px-3 py-2">
                        <CustomSelect
                          options={dropdownOptions.brands}
                          value={colFilters.brand || "All"}
                          onChange={(val) => setCol("brand", val === "All" ? "" : val)}
                          placeholder="All"
                          searchable
                        />
                      </th>
                      <th className="px-3 py-2">
                        <CustomSelect
                          options={CATEGORIES}
                          value={colFilters.category}
                          onChange={(val) => setCol("category", val)}
                          placeholder="All"
                        />
                      </th>
                      <th className="px-3 py-2">
                        <CustomSelect
                          options={["All", "Out (0)", "1 – 5", "6 – 20", "20+"]}
                          value={colFilters.stock}
                          onChange={(val) => setCol("stock", val)}
                          placeholder="All"
                        />
                      </th>
                      <th className="px-3 py-2" />
                      <th className="px-3 py-2">
                        <CustomSelect
                          options={["All", "0", "Under 500", "500 – 1k", "1k – 2.5k", "2.5k+"]}
                          value={colFilters.unitp}
                          onChange={(val) => setCol("unitp", val)}
                          placeholder="All"
                        />
                      </th>
                      <th className="px-3 py-2" />
                      <th className="px-3 py-2">
                        <CustomSelect
                          options={["All", "Active", "Low Stock", "Out of Stock"]}
                          value={colFilters.status}
                          onChange={(val) => setCol("status", val)}
                          placeholder="All"
                        />
                      </th>
                      <th className="px-3 py-2 text-right">
                        <button onClick={() => setColFilters({ vendor: "", name: "", brand: "", category: "All", unit: "", qty: "", stock: "All", unitp: "All", status: "All" })} className="text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-rose-600 transition px-2 py-1 rounded-lg hover:bg-rose-50">
                          Clear Filters
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Dynamic Pinned Filter Totals Row */}
                    <tr className="bg-amber-50/90 border-b-2 border-amber-200/80 font-black">
                      <td colSpan={5} className="py-2.5 px-4 text-xs font-black uppercase tracking-wider text-amber-900">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                          <span>Active Filter Totals ({filteredMetrics.count} Listed Items)</span>
                        </div>
                      </td>
                      <td className="text-right tabular font-black text-xs text-amber-950 py-2.5 px-4 bg-amber-100/70 border-x border-amber-200/60">
                        {filteredMetrics.stockUnits.toLocaleString()} Stock
                      </td>
                      <td className="text-right text-xs text-slate-400 py-2.5 px-4">—</td>
                      <td className="text-right text-xs text-slate-400 py-2.5 px-4">—</td>
                      <td className="text-right tabular font-black text-xs text-emerald-950 py-2.5 px-4 bg-emerald-100/60 border-x border-emerald-200/60">
                        {money(filteredMetrics.totalValuation)}
                      </td>
                      <td className="text-xs text-slate-400 py-2.5 px-4">—</td>
                      <td className="text-center py-2.5 px-4">
                        <span className="text-[9px] font-extrabold uppercase px-2 py-0.5 bg-amber-400 text-slate-950 rounded-md shadow-2xs">
                          TOTALS
                        </span>
                      </td>
                    </tr>
                    {filteredProducts.slice(0, displayLimit).map((p) => {
                      const onHand = mainStockOf(p);
                      const min = p.min_stock ?? 0;
                      const status = statusOf(p);
                      const isOut = status === "Out of Stock";
                      const isLow = status === "Low Stock";
                      const price = p.unit_price || 0;
                      const rowValuation = onHand * price;

                      return (
                        <tr key={p.id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="text-xs font-semibold text-slate-800">{p.vendor_name || "Unassigned"}</td>
                          <td>
                            <div className="font-extrabold text-slate-950 text-xs">{p.name}</div>
                          </td>
                          <td className="text-right tabular font-semibold text-slate-800 text-xs">{p.qty ? `${p.qty} ${qtyUnitOf(p)}` : "—"}</td>
                          <td className="text-xs font-semibold text-slate-900">{p.brand || "Generic"}</td>
                          <td>
                            <span className="lss-badge text-[10px] bg-slate-100 text-slate-800">{p.category || "Retail"}</span>
                          </td>
                          <td className="text-right tabular font-semibold text-slate-700 text-xs">
                            {Number(onHand)}
                          </td>
                          <td className="text-right tabular font-medium text-slate-600 text-xs">{min}</td>
                          <td className="text-right tabular font-medium text-slate-800 text-xs">{money(p.unit_price)}</td>
                          <td className="text-right tabular font-bold text-emerald-950 text-xs bg-emerald-50/50">{money(rowValuation)}</td>
                          <td>
                            <span className={`lss-badge text-[10px] uppercase ${isOut ? "bg-rose-100 text-rose-900 border-rose-300" : isLow ? "bg-amber-100 text-amber-950 border-amber-300" : "bg-emerald-100 text-emerald-900 border-emerald-300"}`}>
                              {status}
                            </span>
                          </td>
                          <td className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => setEditProduct(p)}
                                className="p-1.5 text-amber-700 hover:text-amber-900 hover:bg-amber-100 rounded-lg transition"
                                title="Edit Product Item"
                              >
                                <Edit3 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteProduct(p.id, p.name)}
                                className="p-1.5 text-rose-600 hover:text-rose-900 hover:bg-rose-100 rounded-lg transition"
                                title="Delete Product Item"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredProducts.length > displayLimit && (
                      <tr>
                        <td colSpan={11} className="text-center py-4 bg-slate-50/50">
                          <button
                            onClick={() => setDisplayLimit((prev) => prev + 200)}
                            className="px-4 py-2 bg-white hover:bg-slate-100 border border-slate-300 text-slate-800 font-bold text-xs rounded-xl shadow-xs transition"
                          >
                            Show More Products (Showing {displayLimit} of {filteredProducts.length})
                          </button>
                        </td>
                      </tr>
                    )}
                    {filteredProducts.length === 0 && (
                      <tr>
                        <td colSpan={11} className="text-center py-12 text-slate-500 font-medium">
                          No products match the selected criteria.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeSection === "purchase_invoices" && (
        <PurchaseInvoices
          skus={products}
          onInventoryChanged={() => {
            fetchMasterData();
            fetchKpis();
          }}
          onManualEntry={() => setShowPurchaseInModal(true)}
        />
      )}
      {activeSection === "daily_out" && <DailyStockOut />}
      {activeSection === "ledger" && <StockLedgerPage />}
      {activeSection === "stock_history" && <StockHistory />}

      {/* Render Edit Product Item Modal */}
      {editProduct && (
        <EditProductModal
          product={editProduct}
          onClose={() => setEditProduct(null)}
          onSuccess={() => {
            fetchMasterData();
            fetchKpis();
          }}
        />
      )}

      {/* Render Purchase In Modal */}
      {showPurchaseInModal && (
        <PurchaseInModal
          onClose={() => setShowPurchaseInModal(false)}
          onSuccess={() => {
            fetchMasterData();
            fetchKpis();
          }}
        />
      )}

      {/* Render Add Product Item Modal */}
      {showAddProductModal && (
        <AddProductModal
          onClose={() => setShowAddProductModal(false)}
          onSuccess={() => {
            fetchMasterData();
            fetchKpis();
          }}
        />
      )}
    </div>
  );
}
